import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import {
  NavLink,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import { useSpace } from "../../../contexts/SpaceContext";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Check from "@mui/icons-material/Check";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import VideoLabelIcon from "@mui/icons-material/VideoLabel";
import PrepareWebsite from "./PrepareWebsite";
import TextColors from "./TextColors";
import SessionEndScreen from "./SessionEndScreen";
import ContactForm from "./ContactForm";
import VisibilitySettings from "./VisibilitySettings";
import RequestOptions from "./RequestOptions";

import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import Box from "@mui/material/Box";

import {
  Alert,
  Avatar,
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Divider as MuiDivider,
  FormControl as MuiFormControl,
  Grid,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Call,
  ChatBubble,
  CheckCircle as CheckCircleIcon,
  Work,
} from "@mui/icons-material";

import { CloudUpload as MuiCloudUpload } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { Home, Image, Settings, Star } from "react-feather";
import DisplayOptions from "./DisplayOptions";

// GraphQL Query to fetch widget details for editing
const GET_WIDGET = gql`
  query GetWidget($id: ID!) {
    getWidget(id: $id) {
      id
      business_details {
        name
        display_name
        business_type
        website_url
      }
      website_integration {
        mode
        button_style
        position
        square_buttom
        media
        minimize_small_button
        picture
        preview
        speeach_bubble_test
        call_to_action_button_text
        slogan
      }
      welcome_screen {
        font_shadow
        desc
        customizable_button
        button_color
        font_color
        icon_color
        welcome_message
      }
      contact_options {
        chat_enabled
        phone_enabled
        video_enabled
        audio_enabled
      }
      request_routing {
        preselection_enabled
        automatic_response_enabled
        automatic_response_text
        lead_form_enabled
        privacy_enabled
        privacy_text
      }
      session_end {
        title
        closing_message
        background_color
        text_color
        show_feedback_form
        rating_type
      }
      contact_form {
        unavailable_message
        background_color
        text_color
        form_fields {
          name
          field_type
          enabled
          required
        }
        privacy_enabled
        privacy_text
      }
      visibility_settings {
        always_on
        custom_schedule_enabled
        timezone
        schedule {
          sunday {
            enabled
            slots {
              start
              end
            }
          }
          monday {
            enabled
            slots {
              start
              end
            }
          }
          tuesday {
            enabled
            slots {
              start
              end
            }
          }
          wednesday {
            enabled
            slots {
              start
              end
            }
          }
          thursday {
            enabled
            slots {
              start
              end
            }
          }
          friday {
            enabled
            slots {
              start
              end
            }
          }
          saturday {
            enabled
            slots {
              start
              end
            }
          }
        }
        hide_on_mobile
        hide_when_offline
        dialog_delay_seconds
      }
      script_id
      integrationVerified
    }
  }
`;

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const FormControl = styled(MuiFormControl)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Button = styled(MuiButton)(spacing);

const CloudUpload = styled(MuiCloudUpload)(spacing);

const CenteredContent = styled.div`
  text-align: center;
`;

const BigAvatar = styled(Avatar)`
  width: 120px;
  height: 120px;
  margin: 0 auto ${(props) => props.theme.spacing(2)};
`;

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#784af4",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#784af4",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : "#eaeaf0",
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const QontoStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  color: theme.palette.mode === "dark" ? theme.palette.grey[700] : "#eaeaf0",
  display: "flex",
  height: 22,
  alignItems: "center",
  ...(ownerState.active && {
    color: "#784af4",
  }),
  "& .QontoStepIcon-completedIcon": {
    color: "#784af4",
    zIndex: 1,
    fontSize: 18,
  },
  "& .QontoStepIcon-circle": {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
}));
function QontoStepIcon(props) {
  const { active, completed, className } = props;

  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <Check className="QontoStepIcon-completedIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

QontoStepIcon.propTypes = {
  /**
   * Whether this step is active.
   * @default false
   */
  active: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed: PropTypes.bool,
};
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 25,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient( 95deg,rgb(242,113,33) 0%,rgb(233,64,87) 50%,rgb(138,35,135) 100%)",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : "#eaeaf0",
    borderRadius: 1,
  },
}));
const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[700] : "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  ...(ownerState.active && {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  }),
  ...(ownerState.completed && {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className } = props;

  const icons = {
    1: <Home />,
    2: <Work />,
    3: <Image />,
    4: <Call />,
    5: <Star />,
    6: <ChatBubble />,
    7: <Settings />,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

ColorlibStepIcon.propTypes = {
  /**
   * Whether this step is active.
   * @default false
   */
  active: PropTypes.bool,
  className: PropTypes.string,
  /**
   * Mark the step as completed. Is passed to child components.
   * @default false
   */
  completed: PropTypes.bool,
  /**
   * The label displayed in the step icon.
   */
  icon: PropTypes.node,
};

var steps = [
  "Prepare Website",
  "Display Options",
  "Text & Colors",
  "Request Options",
  "Session End Screen",
  "Contact Form",
  "Visibility Settings",
];

// const steps = ["Prepare Website", "Display Options"];

function CustomizedSteppers({ widgetData, isEditing, widgetId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSpace } = useSpace();

  // Get spaceId from URL params or use current space
  const queryParams = new URLSearchParams(location.search);
  const spaceId = queryParams.get("spaceId") || currentSpace?.id;

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  const saveStepFunctionRef = React.useRef(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [createdWidgetId, setCreatedWidgetId] = React.useState(
    widgetId || null
  );
  const [createdWidgetData, setCreatedWidgetData] = React.useState(
    widgetData || null
  );
  const [integrationPassed, setIntegrationPassed] = React.useState(
    widgetData?.integrationVerified || false
  );

  const isStepOptional = (step) => {
    return step === 1;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = async () => {
    // Save current step data before proceeding (for both edit and add modes)
    if (saveStepFunctionRef.current) {
      try {
        setIsSaving(true);
        const result = await saveStepFunctionRef.current();
        console.log(
          `Step ${activeStep} data saved successfully before proceeding to next step`
        );

        // If this is step 0 (PrepareWebsite) in add mode and we got a widget ID back
        if (!isEditing && activeStep === 0 && result && result.widgetId) {
          setCreatedWidgetId(result.widgetId);
          setCreatedWidgetData(result.widgetData);
          console.log("Widget created with ID:", result.widgetId);
          console.log("Widget data:", result.widgetData);
        } else if (result && result.widgetId) {
          console.log(
            `Step ${activeStep} saved for widget ID:`,
            result.widgetId
          );
        }
      } catch (error) {
        console.error("Error saving step data:", error);
        // Optionally show error message to user
        return; // Don't proceed if save fails
      } finally {
        setIsSaving(false);
      }
    }

    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const [childData, setChildData] = useState("");

  const handleChildData = (data) => {
    setChildData(data);
    console.log(data);
    if (data == "Standard") {
      steps = [
        "Prepare Website",
        "Display Options",
        "Text & Colors",
        "Request Options",
        "Session End Screen",
        "Contact Form",
        "Visibility Settings",
      ];
    } else {
      steps = ["Prepare Website", "Display Options"];
    }
  };

  return (
    <Stack sx={{ width: "100%" }} spacing={4}>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        connector={<ColorlibConnector />}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const labelProps = {};
          if (isStepOptional(index)) {
            labelProps.optional = (
              <Typography variant="caption">Optional</Typography>
            );
          }
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepLabel StepIconComponent={ColorlibStepIcon} {...labelProps}>
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {activeStep === steps.length ? (
        <React.Fragment>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 4,
              mb: 4,
            }}
          >
            <CheckCircleIcon
              sx={{ fontSize: 80, color: "success.main", mb: 2 }}
            />
            <Typography variant="h4" sx={{ mb: 2, color: "success.main" }}>
              Widget Created Successfully!
            </Typography>
            <Alert severity="success" sx={{ mb: 3, maxWidth: 600 }}>
              Your widget has been created and configured successfully.
              {createdWidgetData && (
                <>
                  <br />
                  <strong>Widget Name:</strong>{" "}
                  {createdWidgetData.business_details?.display_name ||
                    createdWidgetData.business_details?.name}
                  <br />
                  <strong>Script ID:</strong> {createdWidgetData.script_id}
                </>
              )}
            </Alert>
            <Typography
              variant="body1"
              sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}
            >
              You can now integrate this widget into your website or manage it
              from the widget list.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              pt: 2,
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                navigate(
                  spaceId
                    ? `/widget/channel-connect?spaceId=${spaceId}&refresh=true`
                    : "/widget/channel-connect?refresh=true"
                )
              }
            >
              View Widget List
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                navigate(
                  spaceId
                    ? `/widget/add-channel-connect?spaceId=${spaceId}`
                    : "/widget/add-channel-connect"
                )
              }
            >
              Create Another Widget
            </Button>
          </Box>
        </React.Fragment>
      ) : activeStep === 0 ? (
        <React.Fragment>
          <PrepareWebsite
            widgetData={createdWidgetData || widgetData}
            isEditing={isEditing}
            widgetId={createdWidgetId || widgetId}
            onSave={(fn) => {
              saveStepFunctionRef.current = fn;
            }}
            onIntegrationResult={(success) => setIntegrationPassed(success)}
            integrationVerified={integrationPassed}
            onWidgetCreated={(widgetId, widgetData) => {
              setCreatedWidgetId(widgetId);
              setCreatedWidgetData(widgetData);
            }}
          />
          {/* <Typography sx={{ mt: 2, mb: 1 }}>Step {activeStep + 1}</Typography> */}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            {isStepOptional(activeStep) && (
              <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                Skip
              </Button>
            )}

            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={
                isSaving ||
                (!isEditing && (!createdWidgetId || !integrationPassed))
              }
            >
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : activeStep === steps.length - 1 ? (
                "Finish"
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </React.Fragment>
      ) : activeStep === 1 ? (
        <React.Fragment>
          <DisplayOptions
            handleChildData={handleChildData}
            widgetData={createdWidgetData || widgetData}
            isEditing={isEditing || !!createdWidgetId}
            widgetId={createdWidgetId || widgetId}
            onSave={(fn) => {
              saveStepFunctionRef.current = fn;
            }}
          />
          {/* <Typography sx={{ mt: 2, mb: 1 }}>Step {activeStep + 1}</Typography> */}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            {isStepOptional(activeStep) && (
              <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                Skip
              </Button>
            )}

            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : activeStep === steps.length - 1 ? (
                "Finish"
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </React.Fragment>
      ) : activeStep === 2 ? (
        <React.Fragment>
          <TextColors
            handleChildData={handleChildData}
            widgetData={createdWidgetData || widgetData}
            isEditing={isEditing || !!createdWidgetId}
            widgetId={createdWidgetId || widgetId}
            onSave={(fn) => {
              saveStepFunctionRef.current = fn;
            }}
          />
          {/* <Typography sx={{ mt: 2, mb: 1 }}>Step {activeStep + 1}</Typography> */}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            {isStepOptional(activeStep) && (
              <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                Skip
              </Button>
            )}

            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : activeStep === steps.length - 1 ? (
                "Finish"
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </React.Fragment>
      ) : activeStep === 3 ? (
        <React.Fragment>
          <RequestOptions
            handleChildData={handleChildData}
            widgetData={createdWidgetData || widgetData}
            isEditing={isEditing || !!createdWidgetId}
            widgetId={createdWidgetId || widgetId}
            onSave={(fn) => {
              saveStepFunctionRef.current = fn;
            }}
          />
          {/* <Typography sx={{ mt: 2, mb: 1 }}>Step {activeStep + 1}</Typography> */}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            {isStepOptional(activeStep) && (
              <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                Skip
              </Button>
            )}

            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : activeStep === steps.length - 1 ? (
                "Finish"
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </React.Fragment>
      ) : activeStep === 4 ? (
        <React.Fragment>
          <SessionEndScreen
            handleChildData={handleChildData}
            widgetData={createdWidgetData || widgetData}
            isEditing={isEditing || !!createdWidgetId}
            widgetId={createdWidgetId || widgetId}
            onSave={(fn) => {
              saveStepFunctionRef.current = fn;
            }}
          />
          {/* <Typography sx={{ mt: 2, mb: 1 }}>Step {activeStep + 1}</Typography> */}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            {isStepOptional(activeStep) && (
              <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                Skip
              </Button>
            )}

            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : activeStep === steps.length - 1 ? (
                "Finish"
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </React.Fragment>
      ) : activeStep === 5 ? (
        <React.Fragment>
          <ContactForm
            handleChildData={handleChildData}
            widgetData={createdWidgetData || widgetData}
            isEditing={isEditing || !!createdWidgetId}
            widgetId={createdWidgetId || widgetId}
            onSave={(fn) => {
              saveStepFunctionRef.current = fn;
            }}
          />
          {/* <Typography sx={{ mt: 2, mb: 1 }}>Step {activeStep + 1}</Typography> */}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            {isStepOptional(activeStep) && (
              <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                Skip
              </Button>
            )}

            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : activeStep === steps.length - 1 ? (
                "Finish"
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </React.Fragment>
      ) : activeStep === 6 ? (
        <React.Fragment>
          <VisibilitySettings
            handleChildData={handleChildData}
            widgetData={createdWidgetData || widgetData}
            isEditing={isEditing || !!createdWidgetId}
            widgetId={createdWidgetId || widgetId}
            onSave={(fn) => {
              saveStepFunctionRef.current = fn;
            }}
          />
          {/* <Typography sx={{ mt: 2, mb: 1 }}>Step {activeStep + 1}</Typography> */}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            {isStepOptional(activeStep) && (
              <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                Skip
              </Button>
            )}

            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : activeStep === steps.length - 1 ? (
                "Finish"
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </React.Fragment>
      ) : (
        <React.Fragment>
          {/* <PrepareWebsite /> */}
          {/* <Typography sx={{ mt: 2, mb: 1 }}>Step {activeStep + 1}</Typography> */}
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Box sx={{ flex: "1 1 auto" }} />
            {isStepOptional(activeStep) && (
              <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                Skip
              </Button>
            )}

            <Button
              onClick={handleNext}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : activeStep === steps.length - 1 ? (
                "Finish"
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Stack>
  );
}

function ChannelConnectAdd() {
  const [searchParams] = useSearchParams();
  const widgetId = searchParams.get("id");
  const isEditing = !!widgetId;

  // Fetch widget data if editing
  const { loading, error, data } = useQuery(GET_WIDGET, {
    variables: { id: widgetId },
    skip: !isEditing, // Skip query if not editing
  });

  const pageTitle = isEditing
    ? "Edit Channel Connect"
    : "Add New Channel Connect";
  const breadcrumbTitle = isEditing
    ? "Edit Channel Connect"
    : "Add New Channel Connect";

  // Show loading state while fetching widget data
  if (isEditing && loading) {
    return (
      <React.Fragment>
        <Helmet title={pageTitle} />
        <Typography>Loading widget data...</Typography>
      </React.Fragment>
    );
  }

  // Show error state if failed to fetch widget data
  if (isEditing && error) {
    return (
      <React.Fragment>
        <Helmet title={pageTitle} />
        <Typography color="error">
          Error loading widget: {error.message}
        </Typography>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Helmet title={pageTitle} />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            {pageTitle}
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/widget/dashboard">
              Dashboard
            </Link>
            <Link component={NavLink} to="/widget/channel-connect">
              Channel Connect List
            </Link>
            <Typography>{breadcrumbTitle}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button
              variant="contained"
              color="inherit"
              sx={{ mx: "0.5rem" }}
              component={Link}
              to="/widget/channel-connect"
            >
              <ArrowBackIcon />
              Back
            </Button>
          </div>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <CustomizedSteppers
            widgetData={isEditing ? data?.getWidget : null}
            isEditing={isEditing}
            widgetId={widgetId}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default ChannelConnectAdd;
